import { KeyboardArrowDown, KeyboardArrowUp, Print } from '@mui/icons-material';
import CircleIcon from '@mui/icons-material/Circle';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import {
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/system';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { BootstrapDialog } from 'src/components/common-modals/BootstrapDialog';
import InfoModal from 'src/components/common-modals/info-modal';
import { AddNoteIcon } from 'src/components/therapyPlanning_2.0/commonComponent/therapy-planning-icons';
import WarningModal from 'src/components/therapyPlanning_2.0/commonComponent/warning-modal';
import {
  updateAppointmentEndTimeAction,
  updateAppointmentEndTimeResetAction,
} from 'src/redux/actions/therapyPlanning';
import { resetEditTaskTimeAction } from 'src/redux/actions/editTaskTimeAction';
import useCurrentUser from 'src/hooks/use-current-user';
import { getPayerCateGroup } from 'src/layouts/components/utils-method';
import SchedulePrintDialog from 'src/pages/therapist-dashboard/todays-activity/SchedulePrintDialog';
import { permissions } from 'src/paths';
import {
  getSplitTaskAction,
  getSplitTaskMinAction,
  resetSplitTaskMinAction,
  editResetSplitTaskMinAction,
} from 'src/redux/actions/schedularTherapistFilterAction';
import TaskSplitMin from './task-split-min';
import {
  getTherapistTaskNoteAction,
  resetCreateTherapistTaskNote,
} from 'src/redux/actions/therapySchedulingSkillList';
import {
  setIsRecurringEditAction,
  resetIsRecurringEditAction,
  cancelRecurringSeriesResetAction,
} from 'src/redux/actions/appointmentAction';
import {
  therapistPatientUnAssignAction,
  therapistPatientUnAssignReset,
} from 'src/redux/actions/therapistPatientUnAssignAction';
import useAxiosInterceptor from 'src/services/interceptor';
import trash from '../../../../icons/untitled-ui/duocolor/trash.svg';
import EditTaskTime from '../../EditTaskTime/editTaskTime';
import {
  assignedPatientTestId,
  confirmCancelTestIds,
  schedulingRowTestId,
  schedulingTestId,
  taskTestId,
  therapistColumnTestId,
} from '../../test-ids';
import AssignDefaultTherapist from './assign-default-therapist-dialog';
import { ClockIcon, ClockTickIcon, CloseIcon, PencilIcon, PersonStarIcon } from './schedule-icon';
import { AppointmentDisplayType, TaskTypeSvg } from './task-type-svg';
import ViewAppointmentsModal from './view-appointments-modal';
import AppointmentSchedulingModal from './appointment-scheduling-modal';
import { appointmentBookFlag } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import TodayIcon from '@mui/icons-material/Today';
import divide from '../../../../icons/untitled-ui/duocolor/divide.svg';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { assignedTimeColor } from './assigned-time-color';
import { SchedulerProgressNoteDueDot } from 'src/components/progressNoteDue/ProgressNoteDueIndicators';
import type { ProgressNoteDue } from 'src/types/progressNoteDue';

function calculateWorkingTime(availableHours: number, productivity: number) {
  // Calculate effective working hours
  const effectiveHours = availableHours * (productivity / 100);

  // Extract hours and minutes
  const hours = Math.floor(effectiveHours);
  const minutes = Math.round((effectiveHours - hours) * 60);

  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

const { ADD_DAILY_TASK, UPDATE_DAILY_TASK, ADD_SCHEDULER_NOTE } = permissions;

// THIN-7758: assignedClockMinutes is the day priced as the therapist's clock time, so a
// concurrent session costs what the therapist actually spends rather than the sum of the
// patients' minutes. groupTherapy and concurrent are per-patient sums and adding them up
// overstates any day with group or concurrent work — they remain the fallback only for a
// server that predates the field.
function assignedMinutes(therapist: any) {
  if (typeof therapist?.assignedClockMinutes === 'number') {
    return therapist.assignedClockMinutes;
  }
  return (
    (therapist?.concurrent || 0) +
    (therapist?.evaluation || 0) +
    (therapist?.groupTherapy || 0) +
    (therapist?.insuranceCall || 0) +
    (therapist?.screening || 0) +
    (therapist?.therapyMinutes || 0)
  );
}

function calculateAssignedTaskMinutes(task: any) {
  if (!task) return;
  const totalTime = assignedMinutes(task);

  const hours = Math.floor(totalTime / 60);
  const minutes = totalTime % 60;

  // Format in h:MM format (padStart ensures two digits for minutes)
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

type Props = {
  therapist: any;
  DeleteTherapistPatient: any;
  providerId: string;
  openEditTimeDialog: any;
  setOpenEditTimeDialog: any;
  index: any;
  updateDailyTasks: any;
  date?: any;
  readDailyTasks: any;
  setIsStatsRefresh?: any;
  isStatsRefresh?: any;
  switchState: 'Productivity Goal' | 'PTIF';
  /**
   * APIT-667. Whether this enterprise uses productivity targets. Optional and defaulted to true
   * where it is destructured, so an omitted prop keeps today's behaviour.
   */
  productivityTargetsEnabled?: boolean;
  handleOpenTaskNote: any;
  setIsEditNote: any;
  setCurrentPatientTask: any;
  therapistsList?: any[];
  editAssignedTaskMin: any;
  selectedTaskType?: any[];
  currentTab?: string;
  currentSkillSelected?: string;
  selectedSortType?: string;
  searchData?: string;
  filterTherapistList?: any[];
};

const calculateTherapistActualHrs = (therapist: any) => {
  // THIN-7758: drives the productivity colour, so it has to be the same clock minutes the
  // assigned-time label shows — otherwise a concurrent day reads as over-booked.
  const actualHrs = assignedMinutes(therapist);
  if (actualHrs === 0) return 0;
  return Math.ceil((actualHrs / 60) * 10) / 10;
};

const TherapistInfoV2 = (props: Props) => {
  const dispatch = useDispatch();
  const {
    DeleteTherapistPatient,
    isStatsRefresh,
    setIsStatsRefresh,
    switchState,
    // APIT-667: defaults to enabled, so a caller that does not pass it is unaffected.
    productivityTargetsEnabled = true,
    handleOpenTaskNote,
    setIsEditNote,
    setCurrentPatientTask,
    editAssignedTaskMin,
  } = props;
  const [therapist, setTherapist] = useState<any>();
  const [therapistTask, setTherapistTask] = useState<any>();

  const [isLoading, setIsLoading] = useState(false);
  const [isTherapistPatient, setIsTherapistPatient] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [InfoMessage, setInfoMessage] = useState('');
  const [therapistId, setTherapistId] = useState('');
  const [patientTaskDetail, setpatientTaskDetail] = useState<any>(null);
  const [therapyInfo, setTherapyInfo] = useState<any>({
    therapyType: '',
    taskType: '',
    taskId: '',
    numberOfMinutes: 0,
    isAssigned: true,
  });
  const [providerId] = useCurrentUser(['providerId']);
  const [openDefaultTherapistDialog, setOpenDefaultTherapistDialog] = useState(false);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [isViewAppointmentsOpen, setIsViewAppointmentsOpen] = useState(false);
  const [isEditAppointmentOpen, setIsEditAppointmentOpen] = useState(false);
  const [selectedAppointmentForEdit, setSelectedAppointmentForEdit] = useState<any>(null);

  // Overlap modal triggered when editing task time hits OVERLAP
  const [overlapModalOpen, setOverlapModalOpen] = useState(false);
  const [overlapPayload, setOverlapPayload] = useState<{
    providerId: any;
    taskUuid: string;
    time: number | string;
  } | null>(null);
  const editTaskTimeState = useSelector((state: any) => state.editTaskTimeReducer);
  const updateAppointmentEndTimeResponse = useSelector(
    (state: any) => state.updateAppointmentEndTimeReducer
  );

  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data
  );
  const therapistPatientUnAssignState = useSelector(
    (state: any) => state?.therapistPatientUnAssignReducer
  );
  const cancelRecurringSeriesState = useSelector(
    (state: any) => state?.cancelRecurringSeriesReducer
  );
  const [isAppointmentBook] = useFeatureToggleWhenReady(appointmentBookFlag, 'FACILITY');

  // Split task mins state
  const [openSplitMin, setOpenSplitMin] = useState(false);
  const [splitMinPatient, setSplitMinPatient] = useState<any>('');
  const [sourceOfSplitMin, setSourceOfSplitMin] = useState('');
  const splitMin = useSelector((state: any) => state.SplitTaskMinReducer);
  const editSplitTaskMin = useSelector((state: any) => state.editSplitTaskMinReducer);

  type SplitMinutes = {
    task1Minutes: number;
    task2Minutes: number;
    uuid: string;
  };

  const [openSplitConfirm, setOpenSplitConfirm] = useState(false);
  const [pendingSplitPayload, setPendingSplitPayload] = useState<SplitMinutes | null>(null);

  const handleSpitTask = (patient: any) => {
    const appointmentIndex =
      patient?._selectedIndex !== undefined
        ? patient._selectedIndex
        : Array.isArray(patient?.scheduleItemType)
        ? patient.scheduleItemType.findIndex((type: string) => type === 'APPOINTMENT')
        : -1;

    if (appointmentIndex === -1) return;

    const taskUuids = patient?.taskUuids;
    const uuid = Array.isArray(taskUuids) ? taskUuids[appointmentIndex] : taskUuids;

    const alteredPatient = {
      uuid,
      // Undo the firstName/lastName swap from the grouping code
      patientFirstname: patient?.patientLastName?.[appointmentIndex] ?? null,
      patientLastname: patient?.patientFirstName?.[appointmentIndex] ?? null,
      tasksType: patient?.taskType,
      numberOfMinutes: Array.isArray(patient?.totalTime)
        ? patient.totalTime[appointmentIndex]
        : patient?.numberOfMinutes,
      patientUuid: patient?.patientUuid,
      discipline: patient?.discipline,
      noteAvailable: patient?.isNoteAvailable?.[appointmentIndex] ?? false,
      splitRef: patient?.splitRef?.[appointmentIndex] ?? null,
      isSupervisorRequired: patient?.isSupervisorRequired?.[appointmentIndex] ?? null,
      scheduleItemType: patient?.scheduleItemType?.[appointmentIndex],
      payerPlanType: patient?.payerType?.[appointmentIndex] ?? null,
      bedAssignment: patient?.bedAssignment ?? '',
      latestCertEndDate: patient?.latestCertEndDate ?? null,
      recurringAppointmentUuid: Array.isArray(patient?.recurringAppointmentUuid)
        ? patient?.recurringAppointmentUuid?.[appointmentIndex] ?? null
        : patient?.recurringAppointmentUuid ?? null,
    };

    setSplitMinPatient(alteredPatient);
    setSourceOfSplitMin('ADD_SPLIT_MIN');
    setOpenSplitMin(true);
  };

  const handleContinueSplitMin = (splitMin: SplitMinutes) => {
    if (sourceOfSplitMin === 'ADD_SPLIT_MIN' && splitMinPatient?.recurringAppointmentUuid) {
      setPendingSplitPayload(splitMin);
      setOpenSplitConfirm(true);
      return;
    }
    dispatch(
      getSplitTaskMinAction({
        providerId: props.providerId,
        data: {
          splitMinute: splitMin.task2Minutes,
          taskUuid: splitMin.uuid,
        },
      })
    );
  };

  const handleConfirmRecurringSplit = () => {
    if (!pendingSplitPayload) return;
    dispatch(
      getSplitTaskMinAction({
        providerId: props.providerId,
        data: {
          splitMinute: pendingSplitPayload.task2Minutes,
          taskUuid: pendingSplitPayload.uuid,
        },
      })
    );
  };

  const handleCancelRecurringSplit = () => {
    if (splitMin?.loading) return;
    setOpenSplitConfirm(false);
    setPendingSplitPayload(null);
  };

  // Close split min dialog on new split success/failure
  useEffect(() => {
    if (splitMin?.response === null && !splitMin.isMinSplit) return;
    if (splitMin?.response !== null && !splitMin.isMinSplit) {
      dispatch(resetSplitTaskMinAction());
      setOpenSplitMin(false);
      setOpenSplitConfirm(false);
      setPendingSplitPayload(null);
      return;
    }
    if (splitMin?.response !== null && splitMin.isMinSplit) {
      setOpenSplitMin(false);
      setOpenSplitConfirm(false);
      setPendingSplitPayload(null);
      if (setIsStatsRefresh) setIsStatsRefresh(true);
      dispatch(resetSplitTaskMinAction());
    }
  }, [splitMin, dispatch, setIsStatsRefresh]);

  // Close split min dialog on edit split success/failure
  useEffect(() => {
    if (editSplitTaskMin?.response === null && !editSplitTaskMin.isTaskMinEdited) return;
    if (editSplitTaskMin?.response !== null && !editSplitTaskMin.isTaskMinEdited) {
      dispatch(editResetSplitTaskMinAction());
      dispatch(resetSplitTaskMinAction());
      setOpenSplitMin(false);
      return;
    }
    if (editSplitTaskMin?.response !== null && editSplitTaskMin.isTaskMinEdited) {
      setOpenSplitMin(false);
      if (setIsStatsRefresh) setIsStatsRefresh(true);
      dispatch(editResetSplitTaskMinAction());
    }
  }, [editSplitTaskMin, dispatch, setIsStatsRefresh]);

  useEffect(() => {
    if (therapistPatientUnAssignState?.isSuccess && isViewAppointmentsOpen) {
      setIsViewAppointmentsOpen(false);
      dispatch(therapistPatientUnAssignReset());
      // Trigger refresh if needed
      if (setIsStatsRefresh) {
        setIsStatsRefresh((prev: any) => !prev);
      }
    }
  }, [
    therapistPatientUnAssignState?.isSuccess,
    isViewAppointmentsOpen,
    dispatch,
    setIsStatsRefresh,
  ]);

  useEffect(() => {
    if (cancelRecurringSeriesState?.success && isViewAppointmentsOpen) {
      setIsViewAppointmentsOpen(false);
      dispatch(cancelRecurringSeriesResetAction());
      if (setIsStatsRefresh) {
        setIsStatsRefresh((prev: any) => !prev);
      }
    }
  }, [cancelRecurringSeriesState?.success, isViewAppointmentsOpen, dispatch, setIsStatsRefresh]);

  // Detect OVERLAP error from edit task time and open confirmation modal
  useEffect(() => {
    if (
      !editTaskTimeState?.loading &&
      !editTaskTimeState?.isTimeEdited &&
      editTaskTimeState?.response?.code === 'OVERLAP'
    ) {
      // Only show the overlap modal for the row that triggered the edit
      // (the EditTaskTime dialog is only open in that one row)
      if (!props?.openEditTimeDialog?.[props?.index]) return;
      const taskUuid =
        editTaskTimeState?.response?.data?.taskUuid ?? editTaskTimeState?.requestPayload?.taskUuid;
      const time =
        editTaskTimeState?.response?.data?.time ?? editTaskTimeState?.requestPayload?.time;
      const overlapProviderId = editTaskTimeState?.requestPayload?.providerId ?? providerId;
      if (taskUuid != null && time != null) {
        setOverlapPayload({ providerId: overlapProviderId, taskUuid, time });
        setOverlapModalOpen(true);
      }
    } else if (overlapModalOpen) {
      // Redux state cleared — close the modal in every instance
      setOverlapModalOpen(false);
      setOverlapPayload(null);
    }
  }, [editTaskTimeState]);

  // Close the modal after update-appointment-end-time succeeds
  useEffect(() => {
    if (updateAppointmentEndTimeResponse?.isSuccess) {
      setOverlapModalOpen(false);
      setOverlapPayload(null);
      dispatch(updateAppointmentEndTimeResetAction());
      dispatch(resetEditTaskTimeAction());
      // Close the EditTaskTime dialog if it's open for this therapist row
      if (props?.openEditTimeDialog?.[props?.index]) {
        const updatedArray = [...props.openEditTimeDialog];
        updatedArray[props.index] = false;
        props.setOpenEditTimeDialog(updatedArray);
      }
      if (setIsStatsRefresh) {
        setIsStatsRefresh((prev: any) => !prev);
      }
    }
  }, [updateAppointmentEndTimeResponse?.isSuccess]);

  const handleOverlapCancel = () => {
    setOverlapModalOpen(false);
    setOverlapPayload(null);
    dispatch(resetEditTaskTimeAction());
  };

  const handleOverlapConfirm = () => {
    if (!overlapPayload) return;
    dispatch(
      updateAppointmentEndTimeAction({
        providerId: overlapPayload.providerId,
        taskUuid: overlapPayload.taskUuid,
        time: overlapPayload.time,
      })
    );
  };

  const handleTaskListResponse = async (providerId: string, therapistId: string, date: any) => {
    const axiosInstance = useAxiosInterceptor();

    try {
      const response = await axiosInstance.get(
        `/provider/v2/${providerId}/scheduling/available-therapist/${therapistId}/stats?localDate=${date}&discipline=${therapist?.discipline}`
      );
      return response;
    } catch (error) {
      return null;
    }
  };
  useEffect(() => {
    if (
      providerId &&
      isTherapistPatient &&
      (Object.keys(therapist?.assignedPatients || {}).length > 0 ||
        (therapist?.futureAdmissions &&
          Array.isArray(therapist.futureAdmissions) &&
          therapist.futureAdmissions.length > 0))
    ) {
      setIsLoading(true);

      const response = handleTaskListResponse(
        typeof providerId === 'string' ? providerId : '',
        therapist?.uuid,
        moment(props.date, 'DD-MM-YYYY').format('YYYY-MM-DD')
      );
      response
        .then((value) => {
          const map = new Map();
          value?.data?.data?.forEach((item: any) => {
            if (item?.taskType !== 'GROUP_THERAPY' && item?.taskType !== 'CONCURRENT') {
              if (map?.has(item?.taskType)) {
                const taskObject = map?.get(item?.taskType);
                map?.set(item?.taskType, {
                  ...taskObject,
                  patientName: [...taskObject?.patientName, item?.patientName],
                  patientUuids: [...(taskObject?.patientUuids || []), item?.patientUuid],
                  isFutureAdmissionList: [
                    ...(taskObject?.isFutureAdmissionList || []),
                    item?.isFutureAdmission === true,
                  ],
                  payerType: [...taskObject.payerType, item?.payerType],
                  totalTime: [...taskObject?.totalTime, item?.totalTime],
                  taskUuids: [...taskObject.taskUuids, item?.taskUuid],
                  isNoteAvailable: [...taskObject.isNoteAvailable, item?.noteAvailable],
                  splitRef: [...taskObject.splitRef, item?.splitRef],
                  patientFirstName: [...taskObject.patientLastName, item?.patientLastName],
                  patientLastName: [...taskObject.patientFirstName, item?.patientFirstName],
                  isSupervisorRequired: [
                    ...taskObject?.isSupervisorRequired,
                    item?.isSupervisorRequired,
                  ],
                  scheduleItemType: [
                    ...(taskObject?.scheduleItemType || []),
                    item?.scheduleItemType,
                  ],
                  recurringAppointmentUuid: [
                    ...(taskObject?.recurringAppointmentUuid || []),
                    item?.recurringAppointmentUuid ?? null,
                  ],
                });
              } else {
                map?.set(item?.taskType, {
                  ...item,
                  patientName: [item?.patientName],
                  patientUuids: [item?.patientUuid],
                  isFutureAdmissionList: [item?.isFutureAdmission === true],
                  payerType: [item?.payerType],
                  totalTime: [item?.totalTime],
                  taskUuids: [item?.taskUuid],
                  isNoteAvailable: [item?.noteAvailable],
                  splitRef: [item?.splitRef],
                  patientFirstName: [item?.patientLastName],
                  patientLastName: [item?.patientFirstName],
                  isSupervisorRequired: [item?.isSupervisorRequired],
                  scheduleItemType: [item?.scheduleItemType],
                  recurringAppointmentUuid: [item?.recurringAppointmentUuid ?? null],
                });
              }
            } else {
              // Group all standalone CONCURRENT items into one bucket and all
              // standalone GROUP_THERAPY items into another (keyed by taskType).
              // When a real groupUuid exists, key by that so genuine groups
              // stay merged. This keeps taskType buckets separate (CONCURRENT
              // vs GROUP_THERAPY don't collide on `null`) while still showing
              // one Concurrent box and one Group box per therapist.
              const bucketKey = item?.groupUuid ? item?.groupUuid : item?.taskType;
              if (map?.get(bucketKey)) {
                const taskObject = map?.get(bucketKey);
                map.set(bucketKey, {
                  ...taskObject,
                  patientName: [...(taskObject?.patientName || []), item?.patientName],
                  patientUuids: [...(taskObject?.patientUuids || []), item?.patientUuid],
                  isFutureAdmissionList: [
                    ...(taskObject?.isFutureAdmissionList || []),
                    item?.isFutureAdmission === true,
                  ],
                  payerType: [...(taskObject?.payerType || []), item?.payerType],
                  totalTime: [...(taskObject?.totalTime || []), item?.numberOfMinutes],
                  taskUuids: [...taskObject.taskUuids, item?.taskUuid],
                  isNoteAvailable: [...taskObject.isNoteAvailable, item?.noteAvailable],
                  splitRef: [...taskObject.splitRef, item?.splitRef],
                  patientFirstName: [...taskObject.patientLastName, item?.patientLastName],
                  patientLastName: [...taskObject.patientFirstName, item?.patientFirstName],
                  isSupervisorRequired: [
                    ...taskObject?.isSupervisorRequired,
                    item?.isSupervisorRequired,
                  ],
                  scheduleItemType: [
                    ...(taskObject?.scheduleItemType || []),
                    item?.scheduleItemType || 'TASK',
                  ],
                  recurringAppointmentUuid: [
                    ...(taskObject?.recurringAppointmentUuid || []),
                    item?.recurringAppointmentUuid ?? null,
                  ],
                });
              } else {
                map?.set(bucketKey, {
                  ...item,
                  patientName: [item?.patientName],
                  patientUuids: [item?.patientUuid],
                  isFutureAdmissionList: [item?.isFutureAdmission === true],
                  payerType: [item?.payerType],
                  totalTime: [item?.numberOfMinutes],
                  taskUuids: [item?.taskUuid],
                  isNoteAvailable: [item?.noteAvailable],
                  splitRef: [item?.splitRef],
                  patientFirstName: [item?.patientLastName],
                  patientLastName: [item?.patientFirstName],
                  isSupervisorRequired: [item?.isSupervisorRequired],
                  scheduleItemType: [item?.scheduleItemType || 'TASK'],
                  recurringAppointmentUuid: [item?.recurringAppointmentUuid ?? null],
                });
              }
            }
          });
          const newTherapistTaskList: any = [];
          map?.forEach((value) => {
            newTherapistTaskList.push(value);
          });

          // Fallback: if the stats endpoint returned nothing but the therapist
          // has assignedPatients (e.g. a lone group/concurrent patient like
          // Davis Stacey → aa Pranali), synthesize tasks from assignedPatients
          // so the expanded view still shows the patient chips.
          if (
            newTherapistTaskList?.length === 0 &&
            therapist?.assignedPatients &&
            Object.keys(therapist?.assignedPatients)?.length > 0
          ) {
            const grouped = new Map<string, any>();
            Object.entries(therapist?.assignedPatients).forEach(
              ([patientUuid, patient]: [string, any]) => {
                const taskType = Array.isArray(patient?.TaskType)
                  ? patient.TaskType[0]
                  : patient?.TaskType || 'THERAPY';
                if (!grouped.has(taskType)) {
                  grouped.set(taskType, {
                    taskType,
                    patientName: [],
                    patientUuids: [],
                    taskUuids: [],
                    totalTime: [],
                    payerType: [],
                    isNoteAvailable: [],
                    splitRef: [],
                    isSupervisorRequired: [],
                    scheduleItemType: [],
                    recurringAppointmentUuid: [],
                    isFutureAdmissionList: [],
                  });
                }
                const entry = grouped.get(taskType);
                entry.patientName.push(patient?.name);
                entry.patientUuids.push(patientUuid);
                entry.taskUuids.push(patientUuid);
                entry.totalTime.push(0);
                entry.payerType.push(patient?.PayerType);
                entry.isNoteAvailable.push(false);
                entry.splitRef.push(patient?.splitRef ?? null);
                entry.isSupervisorRequired.push(!!patient?.isSupervisorRequired);
                entry.scheduleItemType.push('TASK');
                entry.recurringAppointmentUuid.push(patient?.recurringAppointmentUuid ?? null);
                entry.isFutureAdmissionList.push(!!patient?.isFutureAdmission);
              }
            );
            grouped.forEach((value) => newTherapistTaskList.push(value));
          }

          setTherapistTask(newTherapistTaskList);
        })
        .finally(() => {
          setIsStatsRefresh(false);
          setIsLoading(false);
        });
    }
  }, [providerId, isTherapistPatient, isStatsRefresh, therapist]);

  useEffect(() => {
    setTherapist(JSON.parse(JSON.stringify(props.therapist)));
  }, [props.therapist]);

  const actualHrs = useMemo(() => {
    return calculateTherapistActualHrs(therapist);
  }, [therapist]);

  // APIT-667: the assigned-time figure and its clock tick sit in a sibling Box outside the
  // targetProductivity guard below, so the guard that hides the goal figure never reached them
  // and they kept grading the therapist against a target the enterprise had switched off. With
  // targets enabled this is the same isProductiveTime(...) call the two call sites made inline.
  const assignedHrsColor = assignedTimeColor(
    actualHrs,
    therapist?.totalHrs,
    therapist?.targetProductivity,
    productivityTargetsEnabled
  );

  // THIN-7404: warn when the therapist has exactly one concurrent OR one
  // group-therapy patient. Prefer therapist.assignedPatients (authoritative
  // server state); fall back to therapistTask only when assignedPatients
  // hasn't loaded yet.
  const [isSingleConcurrentPatient, isSingleGroupPatient] = useMemo(() => {
    const norm = (v: string) => (v ?? '').toString().trim().toLowerCase();
    const matchesTaskType = (taskTypes: any[] | undefined, keyword: string) =>
      (taskTypes ?? []).some((t: string) => (t ?? '').toString().toUpperCase().includes(keyword));
    const countByTaskType = (taskTypeKey: string, assignedKeyword: string) => {
      const ids = new Set<string>();
      if (therapist?.assignedPatients !== undefined) {
        Object.entries(therapist?.assignedPatients || {}).forEach(([uuid, p]: [string, any]) => {
          if (matchesTaskType(p?.TaskType, assignedKeyword)) {
            ids.add(uuid || norm(p?.name));
          }
        });
      } else {
        (therapistTask ?? [])
          ?.filter((t: any) => t?.taskType === taskTypeKey)
          ?.forEach((t: any) => {
            (t?.patientName ?? [])?.forEach((name: string, i: number) => {
              const key = t?.patientUuids?.[i] || norm(name);
              if (key) ids.add(key);
            });
          });
      }
      return ids.size === 1;
    };
    return [countByTaskType('CONCURRENT', 'CONCURRENT'), countByTaskType('GROUP_THERAPY', 'GROUP')];
  }, [therapist?.assignedPatients, therapistTask]);

  useEffect(() => {
    if (actualHrs === 0) {
      setIsTherapistPatient(false);
    }
  }, [actualHrs]);

  const handleEditMin = (e: any, index: number) => {
    if (!props.updateDailyTasks || !props.readDailyTasks) return;

    if (e?.splitRef[index]) {
      dispatch(
        getSplitTaskAction({
          therapistId: providerId,
          taskUuid: e.taskUuids[index],
        })
      );
      const patientInfo: any = {
        numberOfMinutes: e.totalTime[index],
        uuid: e.taskUuids[index],
        patientName: e.patientName[index],
        tasksType: e.taskType,
        patientFirstname: e.patientFirstName[index],
        patientLastname: e.patientLastName[index],
      };
      editAssignedTaskMin(patientInfo);
      return;
    }
    // For Group / Concurrent, use the per-patient taskUuid + minutes so only
    // the clicked row is edited (previously we sent the group-level totalTime
    // array which rendered as "55,45" in the modal).
    const perIndexMinutes = Array.isArray(e?.totalTime) ? e?.totalTime?.[index] : e?.totalTime;
    setTherapyInfo({
      therapyType: e?.taskType,
      taskType: 'task-uuid',
      taskId: e?.taskUuids?.[index],
      numberOfMinutes: perIndexMinutes,
      isAssigned: true,
    });
    handleEditTimeDialog();
  };

  const handleCloseInfo = () => {
    setOpenModal(false);
  };

  const handleEditTimeDialog = () => {
    const updatedArray = [...props.openEditTimeDialog];
    updatedArray[props.index] = !updatedArray[props.index];
    props.setOpenEditTimeDialog(updatedArray);
  };

  const openDeleteModal = () => {
    handleClose();
    setInfoMessage('Are you sure you want to cancel this assignment?');
    setOpenModal(true);
  };

  const handleSinglePatientDelete = (val: any, index: number) => {
    const updatedPatient = {
      ...val,
      taskUuids: val?.taskUuids[index],
      patientName: val?.patientName[index],
      groupUuid: val?.groupUuid,
      _selectedIndex: index,
    };
    setpatientTaskDetail(updatedPatient);
  };

  const handleTherapistPestientList = () => {
    setIsTherapistPatient((prev) => !prev);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [iseNoteAvailable, setIsNoteAvailable] = useState(false);
  const [isGroupTask, setIsGroupTask] = useState(false);
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    taskUuid: any,
    isNoteAvailable: any
  ) => {
    dispatch(
      getTherapistTaskNoteAction({
        providerId: providerId,
        taskId: taskUuid,
      })
    );
    setIsEditNote(isNoteAvailable);
    setIsNoteAvailable(isNoteAvailable);
    setCurrentPatientTask(taskUuid);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  function sortByName(obj: any) {
    const sortedEntries = Object.entries(obj).sort(([, a]: [any, any], [, b]: [any, any]) => {
      return a?.name?.localeCompare(b?.name);
    });

    const sortedObject = Object.fromEntries(sortedEntries);

    return sortedObject;
  }

  const handlePrint = () => {
    setIsPrintDialogOpen(true);
  };

  const handleClosePrintDialog = () => {
    setIsPrintDialogOpen(false);
  };

  const handleOpenViewAppointments = () => {
    setIsViewAppointmentsOpen(true);
  };

  const handleCloseViewAppointments = () => {
    setIsViewAppointmentsOpen(false);
  };

  const handleEditAppointment = (appointment: any) => {
    // Find the taskId from therapistTask by matching patientUuid or patient name
    let taskId = null;
    let taskType = 'task-uuid';
    let matchedSplitRef: any = null;

    if (appointment?.patientUuid && therapistTask && Array.isArray(therapistTask)) {
      // Search through therapistTask to find matching patient
      for (const task of therapistTask) {
        // Check if this task matches the patient by UUID
        const matchesByUuid = task?.patientUuid === appointment.patientUuid;

        // Check if this task matches the patient by name (handle array structure)
        let matchesByName = false;
        if (
          task?.patientFirstName &&
          task?.patientLastName &&
          appointment?.patientFirstname &&
          appointment?.patientLastname
        ) {
          const taskFirstName = Array.isArray(task.patientFirstName)
            ? task.patientFirstName[0]
            : task.patientFirstName;
          const taskLastName = Array.isArray(task.patientLastName)
            ? task.patientLastName[0]
            : task.patientLastName;

          matchesByName =
            taskFirstName?.toLowerCase() === appointment.patientFirstname?.toLowerCase() &&
            taskLastName?.toLowerCase() === appointment.patientLastname?.toLowerCase();
        }

        // Also check patientName array if available
        let matchesByPatientName = false;
        if (
          task?.patientName &&
          Array.isArray(task.patientName) &&
          appointment?.patientFirstname &&
          appointment?.patientLastname
        ) {
          const fullName = `${appointment.patientLastname}, ${appointment.patientFirstname}`;
          matchesByPatientName = task.patientName.some(
            (name: string) => name?.toLowerCase() === fullName?.toLowerCase()
          );
        }

        if (matchesByUuid || matchesByName || matchesByPatientName) {
          // the Minutes field is conservatively disabled for split-derived assigned tasks.
          const appointmentTaskUuid =
            appointment?.taskUuid || appointment?.taskId || appointment?.uuid;
          let matchedIndex = -1;
          if (appointmentTaskUuid && Array.isArray(task?.taskUuids)) {
            matchedIndex = task.taskUuids.findIndex((uuid: any) => uuid === appointmentTaskUuid);
          }
          if (Array.isArray(task?.splitRef)) {
            matchedSplitRef =
              matchedIndex >= 0
                ? task.splitRef[matchedIndex] ?? null
                : task.splitRef.find((ref: any) => !!ref) ?? null;
          } else {
            matchedSplitRef = task?.splitRef ?? null;
          }

          // Get taskUuid from the task
          if (task?.taskUuids && Array.isArray(task?.taskUuids) && task?.taskUuids?.length > 0) {
            taskId = matchedIndex >= 0 ? task?.taskUuids[matchedIndex] : task?.taskUuids[0];
            taskType = task?.groupUuid ? 'group-uuid' : 'task-uuid';
            break;
          } else if (task?.groupUuid) {
            taskId = task?.groupUuid;
            taskType = 'group-uuid';
            break;
          } else if (task?.taskUuid) {
            taskId = task.taskUuid;
            taskType = 'task-uuid';
            break;
          }
        }
      }

      if (!taskId) {
        console.warn('handleEditAppointment - No matching task found in therapistTask', {
          appointment,
          therapistTaskKeys: therapistTask.map((t: any) => ({
            patientUuid: t?.patientUuid,
            patientName: Array.isArray(t?.patientName) ? t.patientName[0] : t?.patientName,
            hasTaskUuids: !!t?.taskUuids,
            hasGroupUuid: !!t?.groupUuid,
            hasTaskUuid: !!t?.taskUuid,
          })),
        });
      }
    } else {
      console.warn('handleEditAppointment - Cannot lookup taskId:', {
        hasPatientUuid: !!appointment?.patientUuid,
        hasTherapistTask: !!therapistTask,
        isTherapistTaskArray: therapistTask && Array.isArray(therapistTask),
      });
    }

    // Preserve isRecurringEdit from appointment if it exists
    const recurringEditFlag = appointment?.isRecurringEdit || false;

    // Store in Redux to persist across re-renders
    dispatch(setIsRecurringEditAction(recurringEditFlag));

    // Add taskId, taskUuid, and taskType to appointment object
    const appointmentWithTaskId = {
      ...appointment,
      taskId: taskId || appointment?.taskId || appointment?.taskUuid || appointment?.uuid,
      taskUuid: taskId || appointment?.taskUuid || appointment?.uuid,
      taskType: taskType || appointment?.taskType || 'task-uuid',
      isAssigned: appointment?.isAssigned !== undefined ? appointment.isAssigned : true,
      splitRef: appointment?.splitRef ?? matchedSplitRef,
    };

    setSelectedAppointmentForEdit(appointmentWithTaskId);
    setIsViewAppointmentsOpen(false);
    setIsEditAppointmentOpen(true);
  };

  const handleCloseEditAppointment = () => {
    setIsEditAppointmentOpen(false);
    setSelectedAppointmentForEdit(null);
    dispatch(resetIsRecurringEditAction());
  };

  const handleDeleteAppointment = (appointment: any) => {
    if (!appointment?.patientUuid) return;

    dispatch(
      therapistPatientUnAssignAction({
        providerId: props.providerId,
        therapistId: therapist?.uuid || '',
        currentDate: moment(props.date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
        patientId: appointment.patientUuid,
        type: props.selectedTaskType || '',
        active: true,
        searchData: props.searchData || '',
        filterByUuid:
          props.filterTherapistList && props.filterTherapistList.length > 0
            ? props.filterTherapistList
            : null,
        discipline: props.currentTab || '',
        date: props.date,
        skill: props.currentSkillSelected || '',
        sortBy: props.selectedSortType || '',
        meta: {
          providerId: props.providerId,
          type: props.selectedTaskType || '',
          active: true,
          discipline: props.currentTab || '',
          localDate: moment(props.date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
          skill: props.currentSkillSelected || '',
          taskPage: 0,
          therapistPage: 0,
        },
        isV2: true,
      })
    );
  };

  const handleScheduleAppointmentSuccess = () => {
    setIsEditAppointmentOpen(false);
    setSelectedAppointmentForEdit(null);
    // Trigger refresh of therapist stats/task list
    if (setIsStatsRefresh) {
      setIsStatsRefresh((prev: any) => !prev);
    }
  };

  // Check if therapist has any outpatients
  const hasOutPatients = useMemo(() => {
    if (!therapist?.assignedPatients || typeof therapist?.assignedPatients !== 'object') {
      return false;
    }

    return Object.values(therapist?.assignedPatients).some(
      (patient: any) => patient?.isOutPatient === true
    );
  }, [therapist?.assignedPatients]);

  // Check if therapist has any future admissions
  const hasFutureAdmissions = useMemo(() => {
    if (!therapist?.assignedPatients || typeof therapist?.assignedPatients !== 'object') {
      return false;
    }

    return Object.values(therapist?.assignedPatients).some(
      (patient: any) => patient?.isFutureAdmission === true
    );
  }, [therapist?.assignedPatients]);

  // Get outpatients list - match assignedPatients with therapistTask
  const outPatientsList = useMemo(() => {
    if (!hasOutPatients || !therapist?.assignedPatients) return [];

    const outPatients: any[] = [];
    const processedNames = new Set<string>();

    // Create map of outpatient names
    const outpatientNames = new Set<string>();
    Object.values(therapist.assignedPatients).forEach((patient: any) => {
      if (patient?.isOutPatient === true && patient?.name) {
        outpatientNames.add(patient.name.toLowerCase().trim());
      }
    });

    // Find outpatients in therapistTask — exclude rows that are also future admissions
    if (therapistTask && Array.isArray(therapistTask)) {
      therapistTask.forEach((task: any) => {
        if (task?.patientName && Array.isArray(task.patientName)) {
          task.patientName.forEach((patientName: string, index: number) => {
            if (task?.isFutureAdmissionList?.[index] === true) return;
            const normalizedName = patientName.toLowerCase().trim();
            const patientUuid = task?.patientUuids?.[index];
            const taskUuid = task?.taskUuids?.[index];
            const uniqueKey = `${patientUuid ?? normalizedName}_${taskUuid ?? ''}`;
            const matchedByUuid = patientUuid
              ? (therapist.assignedPatients as any)?.[patientUuid]?.isOutPatient === true
              : false;
            const matchedByName = !patientUuid && outpatientNames.has(normalizedName);
            if ((matchedByUuid || matchedByName) && !processedNames.has(uniqueKey)) {
              outPatients.push({
                ...task,
                patientIndex: index,
              });
              processedNames.add(uniqueKey);
            }
          });
        }
      });
    }

    // Add outpatients from assignedPatients that weren't in therapistTask
    const alreadyAddedOutpatientUuids = new Set(
      outPatients.map((op: any) => op?.patientUuids?.[op?.patientIndex]).filter(Boolean)
    );
    Object.entries(therapist.assignedPatients).forEach(([patientId, patient]: [any, any]) => {
      if (patient?.isOutPatient === true && patient?.name) {
        const normalizedName = patient.name.toLowerCase().trim();
        if (alreadyAddedOutpatientUuids.has(patientId)) return;
        if (!processedNames.has(normalizedName)) {
          outPatients.push({
            patientId,
            name: patient?.name,
            PayerType: patient?.PayerType,
            taskType: 'APPOINTMENT',
            taskUuids: [patientId],
            totalTime: [0],
            payerType: [patient?.PayerType],
            patientName: [patient?.name],
            isNoteAvailable: [false],
            isSupervisorRequired: [patient?.isSupervisorRequired || false],
            isCompleted: false,
            patientIndex: 0,
          });
          processedNames.add(normalizedName);
        }
      }
    });

    return outPatients;
  }, [therapist?.assignedPatients, therapistTask, hasOutPatients]);

  // Get future admissions list - match assignedPatients with therapistTask
  const futureAdmissionsList = useMemo(() => {
    if (!hasFutureAdmissions || !therapist?.assignedPatients) return [];

    const futureAdmissions: any[] = [];
    const processedNames = new Set<string>();

    // Create map of future admission names
    const futureAdmissionNames = new Set<string>();
    Object.values(therapist?.assignedPatients).forEach((patient: any) => {
      if (patient?.isFutureAdmission === true && patient?.name) {
        futureAdmissionNames.add(patient?.name.toLowerCase().trim());
      }
    });

    // Find future admissions in therapistTask — match per-row by isFutureAdmissionList/patientUuid
    if (therapistTask && Array.isArray(therapistTask)) {
      therapistTask.forEach((task: any) => {
        if (task?.patientName && Array.isArray(task?.patientName)) {
          task.patientName.forEach((_patientName: string, index: number) => {
            const isFuture = task?.isFutureAdmissionList?.[index] === true;
            const taskUuid = task?.taskUuids?.[index];
            const patientUuid = task?.patientUuids?.[index];
            const uniqueKey = `${patientUuid ?? ''}_${taskUuid ?? ''}`;
            if (isFuture && !processedNames.has(uniqueKey)) {
              futureAdmissions.push({
                ...task,
                patientIndex: index,
              });
              processedNames.add(uniqueKey);
            }
          });
        }
      });
    }

    // Add future admissions from assignedPatients that weren't in therapistTask
    const alreadyAddedFutureUuids = new Set(
      futureAdmissions.map((fa: any) => fa?.patientUuids?.[fa?.patientIndex]).filter(Boolean)
    );
    Object.entries(therapist?.assignedPatients).forEach(([patientId, patient]: [any, any]) => {
      if (patient?.isFutureAdmission === true && patient?.name) {
        const normalizedName = patient?.name.toLowerCase().trim();
        if (alreadyAddedFutureUuids.has(patientId)) return;
        if (!processedNames.has(normalizedName)) {
          futureAdmissions.push({
            patientId,
            name: patient?.name,
            PayerType: patient?.PayerType,
            taskType: 'APPOINTMENT',
            taskUuids: [patientId],
            totalTime: [0],
            payerType: [patient?.PayerType],
            patientName: [patient?.name],
            isNoteAvailable: [false],
            isSupervisorRequired: [patient?.isSupervisorRequired || false],
            isCompleted: false,
            patientIndex: 0,
          });
          processedNames.add(normalizedName);
        }
      }
    });

    return futureAdmissions;
  }, [therapist?.assignedPatients, therapistTask, hasFutureAdmissions]);

  return (
    <>
      <Grid
        container
        sx={{
          borderRadius: '12px',
          minWidth: '322px',
          border: '1px solid #E5E7EB',
          background: '#FFF',
          boxShadow: '0px 1px 10px 0px rgba(0, 0, 0, 0.08)',
          width: '100%',
          alignItems: 'flex-start',
          padding: {
            lg: '5px 10px 10px 10px',
            md: '5px 10px 10px 10px',
            sm: '5px 10px 10px 10px',
            xs: '5px 10px 10px 10px',
          },
          flexDirection: 'column',
          gap: '5px',
          height: '100%',
        }}
      >
        <Grid
          item
          sx={{
            width: '100%',
          }}
        >
          <Grid container>
            <Grid
              item
              sx={{
                padding: '4px 0px',
                display: 'flex',
                justifyContent: 'space-between',
                height: '40px',
                width: '100%',
                alignItems: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  alignSelf: 'stretch',
                  gap: '6px',
                  flexWrap: { lg: 'nowrap', md: 'wrap', sm: 'wrap', xs: 'wrap' },
                }}
              >
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'center'}
                  gap={'6px'}
                >
                  {(isSingleConcurrentPatient || isSingleGroupPatient) && (
                    <Tooltip
                      title={
                        isSingleConcurrentPatient && isSingleGroupPatient
                          ? 'Single Concurrent Patient / Single Group Patient'
                          : isSingleConcurrentPatient
                          ? 'Single Concurrent Patient'
                          : 'Single Group Patient'
                      }
                      placement="top"
                    >
                      <WarningRoundedIcon
                        sx={{ color: '#f50707', fontSize: '18px', flexShrink: 0 }}
                      />
                    </Tooltip>
                  )}
                  <Tooltip
                    title={therapist?.name || ''}
                    disableHoverListener={!therapist?.name || therapist?.name?.length <= 15}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontFamily: 'Inter ',
                        fontSize: '14px ',
                        fontWeight: '600 ',
                        lineHeight: '22px ',
                        maxWidth: '120px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color:
                          isSingleConcurrentPatient || isSingleGroupPatient ? '#f50707' : undefined,
                      }}
                    >
                      {therapist?.name}
                    </Typography>
                  </Tooltip>

                  {/*
                    APIT-667: this guard is the second "NA" the card calls out -- the service no
                    longer sends one, but a therapist with targets off still has a null/0
                    targetProductivity, which would drop the whole row into the NA branch below
                    and hide the PTIF figure that is now the only thing worth showing. With
                    targets off the row therefore always renders the figure, and switchState is
                    pinned to PTIF by the parent, so it is always the PTIF branch.

                    With targets on, `|| !productivityTargetsEnabled` is `|| false`: the
                    condition is exactly the truthiness of targetProductivity, as before.
                  */}
                  {therapist?.targetProductivity || !productivityTargetsEnabled ? (
                    <Tooltip
                      title={
                        switchState === 'Productivity Goal'
                          ? 'Productivity goal'
                          : 'Projected time in facility'
                      }
                    >
                      <Box
                        display={'flex'}
                        alignItems={'center'}
                        gap={'6px'}
                      >
                        {switchState === 'Productivity Goal' ? (
                          <>
                            <Typography
                              sx={{
                                color: '#9747FF',
                                fontFamily: 'Inter ',
                                fontSize: '12px ',
                                fontWeight: '600 ',
                                lineHeight: '14px ',
                                letterSpacing: '0.2px',
                              }}
                            >
                              {therapist?.targetProductivity}
                              {'% = '}

                              {calculateWorkingTime(
                                therapist?.totalHrs,
                                therapist?.targetProductivity
                              )}
                              {'hr'}
                            </Typography>
                            <CircleIcon
                              sx={{ fontSize: 7 }}
                              color="disabled"
                            />
                          </>
                        ) : (
                          <>
                            <Typography
                              sx={{
                                color: '#9747FF',
                                fontFamily: 'Inter ',
                                fontSize: '12px ',
                                fontWeight: '600 ',
                                lineHeight: '14px ',
                                letterSpacing: '0.2px',
                              }}
                            >
                              {'PTIF = '}
                              {therapist?.ptifcal}
                              {'hr'}
                            </Typography>
                            <CircleIcon
                              sx={{ fontSize: 7 }}
                              color="disabled"
                            />
                          </>
                        )}
                      </Box>
                    </Tooltip>
                  ) : (
                    <>
                      <Typography
                        sx={{
                          color: '#9747FF',
                          fontFamily: 'Inter ',
                          fontSize: '12px ',
                          fontWeight: '600 ',
                          lineHeight: '14px ',
                          letterSpacing: '0.2px',
                        }}
                      >
                        NA
                      </Typography>
                      <CircleIcon
                        sx={{ fontSize: 7 }}
                        color="disabled"
                      />
                    </>
                  )}
                </Box>
                <Box
                  display={'flex'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Tooltip title="Assigned time">
                      <Typography
                        sx={{
                          color: assignedHrsColor,
                          fontFamily: 'Inter ',
                          fontSize: '12px ',
                          fontWeight: '600 ',
                          lineHeight: '12px ',
                          letterSpacing: '0.15px',
                        }}
                      >
                        {calculateAssignedTaskMinutes(therapist)}
                        {`hr`}
                      </Typography>
                    </Tooltip>{' '}
                    <ClockTickIcon color={assignedHrsColor} />
                    <Typography sx={{ color: '#757B87', fontSize: '12px', fontWeight: 500 }}>
                      {'/'}
                    </Typography>
                    <Tooltip title="Available time">
                      <Typography
                        sx={{
                          color: '#757B87',
                          fontFamily: 'Inter ',
                          fontSize: '12px ',
                          fontWeight: '600 ',
                          lineHeight: '12px ',
                          letterSpacing: '0.15px',
                        }}
                      >
                        {therapist?.totalHrs}
                        {`hr `}
                      </Typography>
                    </Tooltip>
                    <ClockIcon color="#757B87" />
                  </Box>
                </Box>
              </Box>
              <Box
                display={'flex'}
                alignItems={'center'}
              >
                {hasOutPatients && isAppointmentBook && (
                  <Box>
                    <Tooltip title="View appointments">
                      <IconButton
                        onClick={handleOpenViewAppointments}
                        data-testid={therapistColumnTestId(therapist?.uuid, 'view-appointments')}
                        sx={{ color: '#6366F1', width: '40px', height: '40px' }}
                      >
                        <TodayIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <Box>
                  <Tooltip title="Print daily schedule">
                    <IconButton
                      onClick={handlePrint}
                      disabled={actualHrs === 0}
                      data-testid={therapistColumnTestId(therapist?.uuid, 'print')}
                      sx={{ color: '#6366F1', width: '40px', height: '40px' }}
                    >
                      <Print />
                    </IconButton>
                  </Tooltip>
                </Box>

                <IconButton
                  onClick={handleTherapistPestientList}
                  data-testid={therapistColumnTestId(therapist?.uuid, 'expand')}
                >
                  {isTherapistPatient ? (
                    <KeyboardArrowUp sx={{ color: '#6C737F' }} />
                  ) : (
                    <KeyboardArrowDown sx={{ color: '#6C737F' }} />
                  )}
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        {(actualHrs !== 0 || Object.keys(therapist?.assignedPatients || {})?.length > 0) &&
        !isTherapistPatient ? (
          therapist?.uuid && (
            <Droppable droppableId={therapist?.uuid}>
              {(provided, snapshot) => (
                <Grid
                  {...provided.droppableProps}
                  ref={(el) => {
                    provided.innerRef(el);
                  }}
                  data-testid={therapistColumnTestId(therapist?.uuid, 'dropzone')}
                  item
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    padding: '6px',
                    alignItems: 'center',
                    minHeight: '40px',
                    borderRadius: '8px',
                    width: '100%',
                    gap: '6px',
                    border: snapshot.isDraggingOver
                      ? '1px solid #007bff'
                      : '1px dashed rgba(99, 102, 241, 0.50)',
                    background: snapshot.isDraggingOver ? '#e0f0ff' : '#F8F9FA',
                    position: 'relative',
                    // animation: snapshot.isDraggingOver ? 'dropAnimation 0.5s ease-in-out' : 'none',
                  }}
                >
                  {Object.keys(sortByName(therapist?.assignedPatients))
                    .flatMap((patientId: any) => {
                      const patient = therapist.assignedPatients[patientId];
                      const appointments = patient?.appointments;
                      const hasSplitTasks =
                        Array.isArray(appointments) &&
                        appointments?.length > 1 &&
                        appointments?.some((a: any) => a?.splitRef);
                      if (hasSplitTasks) {
                        return appointments?.map((appointment: any) => ({
                          patientId,
                          patient,
                          chipKey: `${patientId}-${appointment?.taskUuid}`,
                          draggableId: `${patientId}`,
                        }));
                      }
                      return [{ patientId, patient, chipKey: patientId, draggableId: patientId }];
                    })
                    .map(({ patient, chipKey, draggableId }: any, index: number) => (
                      <Draggable
                        key={chipKey}
                        draggableId={`${draggableId}~${index}${therapist?.uuid}`}
                        index={index}
                      >
                        {(provided) => (
                          <Box
                            key={index}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            data-testid={assignedPatientTestId(therapist?.uuid, chipKey)}
                          >
                            <Chip
                              variant="outlined"
                              size="small"
                              label={patient?.name}
                              sx={{
                                backgroundColor: '#E5E7EB',
                                color: '#111927',
                                borderRadius: '20px',
                                borderBottom: `2px solid ${getPayerCateGroup(patient?.PayerType)
                                  ?.borderColor}`,
                                background: 'rgba(17, 25, 39, 0.04)',
                                fontFamily: 'Inter ',
                                fontSize: '12px ',
                                textWrap: 'nowrap',
                                cursor: 'grab',
                              }}
                              key={chipKey}
                            />
                          </Box>
                        )}
                      </Draggable>
                    ))}

                  {therapist?.futureAdmissions &&
                    Array.isArray(therapist?.futureAdmissions) &&
                    therapist?.futureAdmissions.map((futureAdmission: any, index: number) => {
                      const assignedPatientsCount = Object.values(
                        therapist?.assignedPatients || {}
                      ).reduce((count: number, p: any) => {
                        const appts = p?.appointments;
                        const hasSplit =
                          Array.isArray(appts) &&
                          appts?.length > 1 &&
                          appts?.some((a: any) => a?.splitRef);
                        return count + (hasSplit ? appts?.length : 1);
                      }, 0);
                      return (
                        <Draggable
                          key={`future-${futureAdmission?.taskUuid}-${index}`}
                          draggableId={`future-${futureAdmission?.taskUuid}~${
                            assignedPatientsCount + index
                          }${therapist?.uuid}`}
                          index={assignedPatientsCount + index}
                        >
                          {(provided) => (
                            <Box
                              key={`future-${index}`}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              data-testid={schedulingRowTestId(
                                'assigned-future-admission',
                                futureAdmission?.taskUuid
                              )}
                            >
                              <Chip
                                variant="outlined"
                                size="small"
                                label={futureAdmission?.futureAdmissionName}
                                sx={{
                                  backgroundColor: '#E5E7EB',
                                  color: '#111927',
                                  borderRadius: '20px',
                                  borderBottom: '2px solid #535f62',
                                  background: 'rgba(17, 25, 39, 0.04)',
                                  fontFamily: 'Inter ',
                                  fontSize: '12px ',
                                  textWrap: 'nowrap',
                                  cursor: 'grab',
                                }}
                              />
                            </Box>
                          )}
                        </Draggable>
                      );
                    })}

                  {provided.placeholder}
                  <Box sx={{ display: 'flex', position: 'absolute', right: '0px', bottom: '0px' }}>
                    <IconButton
                      onClick={() => setOpenDefaultTherapistDialog(true)}
                      data-testid={therapistColumnTestId(therapist?.uuid, 'default-therapist')}
                      sx={{
                        borderTop: '1px dashed rgba(99, 102, 241, 0.50)',
                        borderLeft: '1px dashed rgba(99, 102, 241, 0.50)',
                        borderRadius: '8px 0px',
                        background: ' #FFF',
                      }}
                      disabled={!permissionData?.currentPermissions?.includes(ADD_DAILY_TASK)}
                    >
                      <PersonStarIcon />
                    </IconButton>
                  </Box>
                </Grid>
              )}
            </Droppable>
          )
        ) : actualHrs === 0 &&
          isTherapistPatient &&
          Object.keys(therapist?.assignedPatients || {})?.length === 0 ? (
          therapist?.uuid && (
            <Droppable droppableId={therapist?.uuid}>
              {(provided, snapshot) => (
                <Grid
                  {...provided.droppableProps}
                  ref={(el) => {
                    provided.innerRef(el);
                  }}
                  data-testid={therapistColumnTestId(therapist?.uuid, 'dropzone')}
                  item
                  sx={{
                    display: 'flex',
                    padding: '6px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '90px',
                    borderRadius: '8px',
                    border: snapshot.isDraggingOver
                      ? '1px solid #007bff'
                      : '1px dashed rgba(99, 102, 241, 0.50)',
                    background: snapshot.isDraggingOver ? '#e0f0ff' : '#F8F9FA',
                    width: '100%',
                    position: 'relative',
                    // animation: snapshot.isDraggingOver ? 'dropAnimation 0.5s ease-in-out' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                      color: 'rgba(17, 25, 39, 0.38)',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                    }}
                  >
                    Drag and Drop Patients
                  </Typography>
                  {provided.placeholder}
                </Grid>
              )}
            </Droppable>
          )
        ) : isTherapistPatient ? (
          isLoading ? (
            <CircularProgress
              sx={{ margin: 'auto' }}
              color="info"
            />
          ) : (
            therapist?.uuid && (
              <Droppable droppableId={therapist?.uuid}>
                {(provided) => (
                  <Grid
                    ref={provided.innerRef}
                    key={therapist?.uuid}
                    {...provided.droppableProps}
                    data-testid={therapistColumnTestId(therapist?.uuid, 'dropzone')}
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      ':hover': {
                        cursor: 'grab',
                      },
                    }}
                  >
                    {therapistTask
                      ?.filter((task: any) => {
                        // Filter out tasks where every row is an outpatient OR every row is a future admission.
                        // Future-admission filtering must run regardless of the
                        // isAppointmentBook flag — otherwise pre-admit patients
                        // leak into the main task list in environments where the
                        // flag is off.
                        if (!therapist?.assignedPatients) return true;
                        if (
                          !task?.patientName ||
                          !Array.isArray(task.patientName) ||
                          task.patientName.length === 0
                        ) {
                          return true;
                        }

                        const isFutureFlags: boolean[] = Array.isArray(task?.isFutureAdmissionList)
                          ? task.isFutureAdmissionList
                          : [];
                        const patientUuids: string[] = Array.isArray(task?.patientUuids)
                          ? task.patientUuids
                          : [];

                        const isOutpatientAt = (index: number) => {
                          const uuid = patientUuids[index];
                          if (uuid) {
                            return (
                              (therapist.assignedPatients as any)?.[uuid]?.isOutPatient === true
                            );
                          }
                          return false;
                        };

                        const allAreFutureAdmissions =
                          hasFutureAdmissions &&
                          task.patientName.every(
                            (_p: string, idx: number) => isFutureFlags[idx] === true
                          );
                        const allAreOutpatients =
                          isAppointmentBook &&
                          hasOutPatients &&
                          task.patientName.every((_p: string, idx: number) => isOutpatientAt(idx));

                        return !allAreOutpatients && !allAreFutureAdmissions;
                      })
                      .map((single_Patient: any, index: number) => (
                        <Grid
                          key={index}
                          container
                          flexDirection={'column'}
                          gap={1}
                          sx={{
                            display: 'flex',
                            padding: '6px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            minHeight: '40px',
                            borderRadius: '8px',
                            border: '1px dashed rgba(99, 102, 241, 0.50)',
                            // width: 'fit-content',
                            width: '100%',
                            flexWrap: 'nowrap',
                            overflow: 'hidden',
                            pointerEvents: !single_Patient?.isCompleted ? 'inherit' : 'none',
                            backgroundColor: 'white',
                          }}
                        >
                          <Grid
                            item
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              width: '94%',
                              margin: 'auto',
                              opacity: !single_Patient?.isCompleted ? '100%' : '17%',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '6px',
                              }}
                            >
                              {single_Patient?.taskType && (
                                <React.Fragment>
                                  <TaskTypeSvg
                                    isTaskIcon={true}
                                    taskType={single_Patient?.taskType.replace('_', ' ')}
                                  />
                                </React.Fragment>
                              )}
                              <Typography sx={{ fontSize: '14px', color: '#4338CA' }}>
                                {((single_Patient?.taskType === 'CONCURRENT' ||
                                  single_Patient?.taskType === 'GROUP_THERAPY') &&
                                  single_Patient?.groupName) ||
                                  AppointmentDisplayType[
                                    single_Patient?.taskType as keyof typeof AppointmentDisplayType
                                  ]}
                              </Typography>
                              <IconButton
                                onClick={() => {
                                  setTherapistId(therapist?.uuid);
                                  setpatientTaskDetail(single_Patient);
                                  openDeleteModal();
                                }}
                                data-testid={schedulingRowTestId(
                                  'assigned-group',
                                  single_Patient?.taskUuids?.[0],
                                  'cancel-assignment'
                                )}
                                disabled={
                                  !permissionData?.currentPermissions?.includes(ADD_DAILY_TASK)
                                }
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          </Grid>
                          <Box
                            sx={{ display: 'flex', flexWrap: 'wrap', width: '100%', gap: '8px' }}
                          >
                            {single_Patient?.patientName
                              ?.map((patient: any, index: number) => {
                                // Future-admission rows should never appear in
                                // the main task list — skip regardless of the
                                // isAppointmentBook flag, otherwise pre-admit
                                // patients leak in as normal EVAL/THERAPY rows
                                // (seen in production when the flag is off).
                                if (single_Patient?.isFutureAdmissionList?.[index] === true) {
                                  return null;
                                }
                                if (therapist?.assignedPatients) {
                                  const patientUuid = single_Patient?.patientUuids?.[index];
                                  const patientEntry = patientUuid
                                    ? (therapist.assignedPatients as any)?.[patientUuid] ??
                                      Object.values(therapist.assignedPatients).find(
                                        (p: any) => p?.patientUuid === patientUuid
                                      )
                                    : Object.values(therapist.assignedPatients).find(
                                        (assignedPatient: any) =>
                                          assignedPatient?.name?.toLowerCase().trim() ===
                                          patient?.toLowerCase().trim()
                                      );
                                  if (patientEntry) {
                                    // Always skip future admissions
                                    if ((patientEntry as any)?.isFutureAdmission === true) {
                                      return null;
                                    }
                                    // Outpatient skip is still feature-gated
                                    if (
                                      isAppointmentBook &&
                                      (patientEntry as any)?.isOutPatient === true
                                    ) {
                                      return null;
                                    }
                                  }
                                }
                                return { patient, index };
                              })
                              ?.filter((item: any) => item !== null)
                              ?.map(({ patient, index }: { patient: any; index: number }) => (
                                <Draggable
                                  key={single_Patient?.taskUuids[index]}
                                  draggableId={`${single_Patient?.taskUuids[index]}+${index}+${single_Patient?.isSupervisorRequired[index]}`}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <Stack
                                      key={index}
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      data-testid={taskTestId(single_Patient?.taskUuids?.[index])}
                                      sx={{
                                        '&:hover': {
                                          background: 'rgba(17, 25, 39, 0.04)',
                                        },
                                        borderRadius: '12px',
                                        padding: '15px 0px 15px 10px',
                                        height: '22px',
                                        alignItems: 'center',
                                        border: snapshot.isDragging ? '1px solid #007bff' : 'none',
                                        background: snapshot.isDragging ? '#e0f0ff' : '#1119270A',
                                        borderBottom: `2px solid ${getPayerCateGroup(
                                          single_Patient?.payerType[index]
                                        )?.borderColor}`,
                                        // animation: snapshot.isDragging
                                        //   ? 'dropAnimation 0.5s ease-in-out'
                                        //   : 'none',
                                        //commented the code, because of this it lags when performing fast operation
                                      }}
                                      flexDirection={'row'}
                                    >
                                      {/* THIN-8145: the card's progress-note markers survive the drop as dots (up to two, THIN-8192) */}
                                      {(
                                        (therapist?.assignedPatients?.[
                                          single_Patient?.patientUuids?.[index]
                                        ]?.progressNotesDue as ProgressNoteDue[] | undefined) ?? []
                                      ).map((due) => (
                                        <SchedulerProgressNoteDueDot
                                          key={due.dueDate}
                                          due={due}
                                          date={moment(props.date, 'DD-MM-YYYY').format(
                                            'YYYY-MM-DD'
                                          )}
                                        />
                                      ))}
                                      <Typography
                                        sx={{
                                          fontFamily: 'Inter ',
                                          fontSize: '13px ',
                                          fontWeight: '500 ',
                                          letterSpacing: '0.14px',
                                        }}
                                      >
                                        {`${patient}`}
                                      </Typography>
                                      <Box
                                        onClick={() => {
                                          setTherapistId(therapist?.uuid);
                                          handleEditMin(single_Patient, index);
                                          handleSinglePatientDelete(single_Patient, index);
                                        }}
                                        data-testid={taskTestId(
                                          single_Patient?.taskUuids?.[index],
                                          'minutes'
                                        )}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          cursor: 'pointer',
                                          gap: '5px',
                                        }}
                                      >
                                        <Typography
                                          noWrap={true}
                                          sx={{
                                            fontSize: '13px',
                                            fontWeight: 500,
                                            display: 'flex',
                                            width: '100%',
                                            color: '#111927',
                                            pl: '8px',
                                          }}
                                        >
                                          {Array.isArray(single_Patient?.totalTime)
                                            ? single_Patient?.totalTime[index]
                                            : single_Patient?.totalTime}
                                          &nbsp;min
                                        </Typography>
                                        {!snapshot.isDragging && <PencilIcon forSingleCard />}
                                      </Box>
                                      {!snapshot.isDragging && (
                                        <IconButton
                                          id="basic-button"
                                          aria-controls={open ? 'basic-menu' : undefined}
                                          aria-haspopup="true"
                                          aria-expanded={open ? 'true' : undefined}
                                          onClick={(e) => {
                                            setIsGroupTask(false);
                                            setTherapistId(therapist?.uuid);
                                            handleSinglePatientDelete(single_Patient, index);
                                            handleClick(
                                              e,
                                              single_Patient?.taskUuids[index],
                                              single_Patient?.isNoteAvailable[index]
                                            );
                                          }}
                                          data-testid={taskTestId(
                                            single_Patient?.taskUuids?.[index],
                                            'menu'
                                          )}
                                          disabled={
                                            !permissionData?.currentPermissions?.includes(
                                              ADD_DAILY_TASK
                                            )
                                          }
                                        >
                                          <MoreVertRoundedIcon
                                            sx={{
                                              fontSize: '18px',
                                            }}
                                          />
                                        </IconButton>
                                      )}
                                    </Stack>
                                  )}
                                </Draggable>
                              ))}
                          </Box>
                        </Grid>
                      ))}
                    {/* Appointment Box for Outpatients */}
                    {hasOutPatients && isAppointmentBook && outPatientsList.length > 0 && (
                      <Grid
                        container
                        flexDirection={'column'}
                        gap={1}
                        sx={{
                          display: 'flex',
                          padding: '6px',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          minHeight: '40px',
                          borderRadius: '8px',
                          border: '1px dashed rgba(99, 102, 241, 0.50)',
                          width: '100%',
                          flexWrap: 'nowrap',
                          overflow: 'hidden',
                          backgroundColor: 'white',
                        }}
                      >
                        <Grid
                          item
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '94%',
                            margin: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '6px',
                            }}
                          >
                            <TaskTypeSvg
                              isTaskIcon={true}
                              taskType="APPOINTMENT"
                            />
                            <Typography sx={{ fontSize: '14px', color: '#4338CA' }}>
                              {AppointmentDisplayType.APPOINTMENT}
                            </Typography>
                            <IconButton
                              onClick={() => {}}
                              disabled={
                                !permissionData?.currentPermissions?.includes(ADD_DAILY_TASK)
                              }
                              sx={{ opacity: 0 }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        </Grid>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', width: '100%', gap: '8px' }}>
                          {outPatientsList.map((outPatient: any, listIndex: number) => {
                            const patientIndex =
                              outPatient.patientIndex !== undefined ? outPatient.patientIndex : 0;
                            const patientName = Array.isArray(outPatient.patientName)
                              ? outPatient.patientName[patientIndex]
                              : outPatient.patientName || outPatient.name;
                            const totalTime = Array.isArray(outPatient.totalTime)
                              ? outPatient.totalTime[patientIndex]
                              : (Array.isArray(outPatient.totalTime)
                                  ? outPatient.totalTime[0]
                                  : outPatient.totalTime) || 0;
                            const taskUuid = Array.isArray(outPatient.taskUuids)
                              ? outPatient.taskUuids[patientIndex]
                              : (Array.isArray(outPatient.taskUuids)
                                  ? outPatient.taskUuids[0]
                                  : outPatient.taskUuids) || outPatient.patientId;
                            const payerType = Array.isArray(outPatient.payerType)
                              ? outPatient.payerType[patientIndex]
                              : (Array.isArray(outPatient.payerType)
                                  ? outPatient.payerType[0]
                                  : outPatient.payerType) || outPatient.PayerType;
                            const isNoteAvailable = Array.isArray(outPatient.isNoteAvailable)
                              ? outPatient.isNoteAvailable[patientIndex]
                              : (Array.isArray(outPatient.isNoteAvailable)
                                  ? outPatient.isNoteAvailable[0]
                                  : outPatient.isNoteAvailable) || false;
                            const isSupervisorRequired = Array.isArray(
                              outPatient.isSupervisorRequired
                            )
                              ? outPatient.isSupervisorRequired[patientIndex]
                              : (Array.isArray(outPatient.isSupervisorRequired)
                                  ? outPatient.isSupervisorRequired[0]
                                  : outPatient.isSupervisorRequired) || false;

                            const outpatientTask = {
                              ...outPatient,
                              taskType: 'APPOINTMENT',
                              taskUuids: Array.isArray(outPatient.taskUuids)
                                ? outPatient.taskUuids
                                : [outPatient.taskUuids || outPatient.patientId],
                              totalTime: Array.isArray(outPatient.totalTime)
                                ? outPatient.totalTime
                                : [outPatient.totalTime || 0],
                              payerType: Array.isArray(outPatient.payerType)
                                ? outPatient.payerType
                                : [outPatient.payerType || outPatient.PayerType],
                              patientName: Array.isArray(outPatient.patientName)
                                ? outPatient.patientName
                                : [outPatient.patientName || outPatient.name],
                              isNoteAvailable: Array.isArray(outPatient.isNoteAvailable)
                                ? outPatient.isNoteAvailable
                                : [outPatient.isNoteAvailable || false],
                              isSupervisorRequired: Array.isArray(outPatient.isSupervisorRequired)
                                ? outPatient.isSupervisorRequired
                                : [outPatient.isSupervisorRequired || false],
                            };

                            return (
                              <Draggable
                                key={`${taskUuid}-${listIndex}`}
                                draggableId={`${taskUuid}+${listIndex}+${isSupervisorRequired}`}
                                index={listIndex}
                              >
                                {(provided, snapshot) => (
                                  <Stack
                                    key={listIndex}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    data-testid={taskTestId(taskUuid)}
                                    sx={{
                                      '&:hover': {
                                        background: 'rgba(17, 25, 39, 0.04)',
                                      },
                                      borderRadius: '12px',
                                      padding: '15px 0px 15px 10px',
                                      height: '22px',
                                      alignItems: 'center',
                                      border: snapshot.isDragging ? '1px solid #007bff' : 'none',
                                      background: snapshot.isDragging ? '#e0f0ff' : '#1119270A',
                                      borderBottom: `2px solid ${getPayerCateGroup(payerType)
                                        ?.borderColor}`,
                                    }}
                                    flexDirection={'row'}
                                  >
                                    <Typography
                                      sx={{
                                        fontFamily: 'Inter ',
                                        fontSize: '13px ',
                                        fontWeight: '500 ',
                                        letterSpacing: '0.14px',
                                      }}
                                    >
                                      {patientName}
                                    </Typography>
                                    <Box
                                      onClick={() => {
                                        setTherapistId(therapist?.uuid);
                                        handleEditMin(outpatientTask, patientIndex);
                                        handleSinglePatientDelete(outpatientTask, patientIndex);
                                      }}
                                      data-testid={taskTestId(taskUuid, 'minutes')}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        gap: '5px',
                                      }}
                                    >
                                      <Typography
                                        noWrap={true}
                                        sx={{
                                          fontSize: '13px',
                                          fontWeight: 500,
                                          display: 'flex',
                                          width: '100%',
                                          color: '#111927',
                                          pl: '8px',
                                        }}
                                      >
                                        {totalTime}&nbsp;min
                                      </Typography>
                                      {!snapshot.isDragging && <PencilIcon forSingleCard />}
                                    </Box>
                                    {!snapshot.isDragging && (
                                      <IconButton
                                        id="basic-button"
                                        aria-controls={open ? 'basic-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={open ? 'true' : undefined}
                                        onClick={(e) => {
                                          setIsGroupTask(false);
                                          setTherapistId(therapist?.uuid);
                                          handleSinglePatientDelete(outpatientTask, patientIndex);
                                          handleClick(e, taskUuid, isNoteAvailable);
                                        }}
                                        data-testid={taskTestId(taskUuid, 'menu')}
                                        disabled={
                                          !permissionData?.currentPermissions?.includes(
                                            ADD_DAILY_TASK
                                          )
                                        }
                                      >
                                        <MoreVertRoundedIcon
                                          sx={{
                                            fontSize: '18px',
                                          }}
                                        />
                                      </IconButton>
                                    )}
                                  </Stack>
                                )}
                              </Draggable>
                            );
                          })}
                        </Box>
                      </Grid>
                    )}
                    {/* Future Admission Box */}
                    {hasFutureAdmissions && futureAdmissionsList?.length > 0 && (
                      <Grid
                        container
                        flexDirection={'column'}
                        gap={1}
                        sx={{
                          display: 'flex',
                          padding: '6px',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          minHeight: '40px',
                          borderRadius: '8px',
                          border: '1px dashed rgba(99, 102, 241, 0.50)',
                          width: '100%',
                          flexWrap: 'nowrap',
                          overflow: 'hidden',
                          backgroundColor: 'white',
                        }}
                      >
                        <Grid
                          item
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '94%',
                            margin: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '6px',
                            }}
                          >
                            <TaskTypeSvg
                              isTaskIcon={true}
                              taskType="APPOINTMENT"
                            />
                            <Typography sx={{ fontSize: '14px', color: '#4338CA' }}>
                              Future Admission
                            </Typography>
                            <IconButton
                              onClick={() => {}}
                              disabled={
                                !permissionData?.currentPermissions?.includes(ADD_DAILY_TASK)
                              }
                              sx={{ opacity: 0 }}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        </Grid>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', width: '100%', gap: '8px' }}>
                          {futureAdmissionsList?.map((futureAdmission: any, listIndex: number) => {
                            const patientIndex =
                              futureAdmission?.patientIndex !== undefined
                                ? futureAdmission?.patientIndex
                                : 0;
                            const patientName = Array.isArray(futureAdmission?.patientName)
                              ? futureAdmission?.patientName[patientIndex]
                              : futureAdmission?.patientName || futureAdmission?.name;
                            const totalTime = Array.isArray(futureAdmission?.totalTime)
                              ? futureAdmission?.totalTime[patientIndex]
                              : (Array.isArray(futureAdmission?.totalTime)
                                  ? futureAdmission?.totalTime[0]
                                  : futureAdmission?.totalTime) || 0;
                            const taskUuid = Array.isArray(futureAdmission?.taskUuids)
                              ? futureAdmission?.taskUuids[patientIndex]
                              : (Array.isArray(futureAdmission?.taskUuids)
                                  ? futureAdmission?.taskUuids[0]
                                  : futureAdmission?.taskUuids) || futureAdmission?.patientId;
                            const payerType = Array.isArray(futureAdmission?.payerType)
                              ? futureAdmission?.payerType[patientIndex]
                              : (Array.isArray(futureAdmission?.payerType)
                                  ? futureAdmission?.payerType[0]
                                  : futureAdmission?.payerType) || futureAdmission?.PayerType;
                            const isNoteAvailable = Array.isArray(futureAdmission?.isNoteAvailable)
                              ? futureAdmission?.isNoteAvailable[patientIndex]
                              : (Array.isArray(futureAdmission?.isNoteAvailable)
                                  ? futureAdmission?.isNoteAvailable[0]
                                  : futureAdmission?.isNoteAvailable) || false;
                            const isSupervisorRequired = Array.isArray(
                              futureAdmission?.isSupervisorRequired
                            )
                              ? futureAdmission?.isSupervisorRequired[patientIndex]
                              : (Array.isArray(futureAdmission?.isSupervisorRequired)
                                  ? futureAdmission?.isSupervisorRequired[0]
                                  : futureAdmission?.isSupervisorRequired) || false;

                            const futureAdmissionTask = {
                              ...futureAdmission,
                              taskType: 'APPOINTMENT',
                              taskUuids: Array.isArray(futureAdmission?.taskUuids)
                                ? futureAdmission?.taskUuids
                                : [futureAdmission?.taskUuids || futureAdmission?.patientId],
                              totalTime: Array.isArray(futureAdmission?.totalTime)
                                ? futureAdmission?.totalTime
                                : [futureAdmission?.totalTime || 0],
                              payerType: Array.isArray(futureAdmission?.payerType)
                                ? futureAdmission?.payerType
                                : [futureAdmission?.payerType || futureAdmission?.PayerType],
                              patientName: Array.isArray(futureAdmission?.patientName)
                                ? futureAdmission?.patientName
                                : [futureAdmission?.patientName || futureAdmission?.name],
                              isNoteAvailable: Array.isArray(futureAdmission?.isNoteAvailable)
                                ? futureAdmission?.isNoteAvailable
                                : [futureAdmission?.isNoteAvailable || false],
                              isSupervisorRequired: Array.isArray(
                                futureAdmission?.isSupervisorRequired
                              )
                                ? futureAdmission?.isSupervisorRequired
                                : [futureAdmission?.isSupervisorRequired || false],
                            };

                            return (
                              <Draggable
                                key={`${taskUuid}-${listIndex}`}
                                draggableId={`${taskUuid}+${listIndex}+${isSupervisorRequired}`}
                                index={listIndex}
                              >
                                {(provided, snapshot) => (
                                  <Stack
                                    key={listIndex}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    data-testid={taskTestId(taskUuid)}
                                    sx={{
                                      '&:hover': {
                                        background: 'rgba(17, 25, 39, 0.04)',
                                      },
                                      borderRadius: '12px',
                                      padding: '15px 0px 15px 10px',
                                      height: '22px',
                                      alignItems: 'center',
                                      border: snapshot.isDragging ? '1px solid #007bff' : 'none',
                                      background: snapshot.isDragging ? '#e0f0ff' : '#1119270A',
                                      borderBottom: `2px solid ${getPayerCateGroup(payerType)
                                        ?.borderColor}`,
                                    }}
                                    flexDirection={'row'}
                                  >
                                    <Typography
                                      sx={{
                                        fontFamily: 'Inter ',
                                        fontSize: '13px ',
                                        fontWeight: '500 ',
                                        letterSpacing: '0.14px',
                                      }}
                                    >
                                      {patientName}
                                    </Typography>
                                    <Box
                                      onClick={() => {
                                        setTherapistId(therapist?.uuid);
                                        handleEditMin(futureAdmissionTask, patientIndex);
                                        handleSinglePatientDelete(
                                          futureAdmissionTask,
                                          patientIndex
                                        );
                                      }}
                                      data-testid={taskTestId(taskUuid, 'minutes')}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        gap: '5px',
                                      }}
                                    >
                                      <Typography
                                        noWrap={true}
                                        sx={{
                                          fontSize: '13px',
                                          fontWeight: 500,
                                          display: 'flex',
                                          width: '100%',
                                          color: '#111927',
                                          pl: '8px',
                                        }}
                                      >
                                        {totalTime}&nbsp;min
                                      </Typography>
                                      {!snapshot.isDragging && <PencilIcon forSingleCard />}
                                    </Box>
                                    {!snapshot.isDragging && (
                                      <IconButton
                                        id="basic-button"
                                        aria-controls={open ? 'basic-menu' : undefined}
                                        aria-haspopup="true"
                                        aria-expanded={open ? 'true' : undefined}
                                        onClick={(e) => {
                                          setIsGroupTask(false);
                                          setTherapistId(therapist?.uuid);
                                          handleSinglePatientDelete(
                                            futureAdmissionTask,
                                            patientIndex
                                          );
                                          handleClick(e, taskUuid, isNoteAvailable);
                                        }}
                                        data-testid={taskTestId(taskUuid, 'menu')}
                                        disabled={
                                          !permissionData?.currentPermissions?.includes(
                                            ADD_DAILY_TASK
                                          )
                                        }
                                      >
                                        <MoreVertRoundedIcon
                                          sx={{
                                            fontSize: '18px',
                                          }}
                                        />
                                      </IconButton>
                                    )}
                                  </Stack>
                                )}
                              </Draggable>
                            );
                          })}
                        </Box>
                      </Grid>
                    )}
                    {provided.placeholder}
                  </Grid>
                )}
              </Droppable>
            )
          )
        ) : (
          therapist?.uuid && (
            <Droppable droppableId={therapist?.uuid}>
              {(provided, snapshot) => (
                <Grid
                  {...provided.droppableProps}
                  ref={(el) => {
                    provided.innerRef(el);
                  }}
                  data-testid={therapistColumnTestId(therapist?.uuid, 'dropzone')}
                  item
                  sx={{
                    display: 'flex',
                    padding: '6px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '40px',
                    borderRadius: '8px',
                    // marginTop: '1rem',
                    border: snapshot.isDraggingOver
                      ? '1px solid #007bff' // Style when item is being dragged
                      : '1px dashed rgba(99, 102, 241, 0.50)',
                    background: snapshot.isDraggingOver ? '#e0f0ff' : '#F8F9FA',
                    width: '100%',
                    position: 'relative',
                    // animation: snapshot.isDraggingOver ? 'dropAnimation 0.5s ease-in-out' : 'none',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                      color: 'rgba(17, 25, 39, 0.38)',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                    }}
                  >
                    Drag and Drop Patients
                  </Typography>
                  {provided.placeholder}
                </Grid>
              )}
            </Droppable>
          )
        )}
      </Grid>
      <BootstrapDialog
        onClose={handleCloseInfo}
        aria-labelledby="customized-dialog-title"
        open={openModal}
      >
        <InfoModal
          close={handleCloseInfo}
          title="Cancel assignment?"
          message={InfoMessage}
          button={{ saveLabel: 'Yes', closeLabel: 'No' }}
          displayTime={false}
          testIds={confirmCancelTestIds('cancel-assignment')}
          handleSaveBtn={() => {
            handleCloseInfo();
            // If scheduleItemType array exists, filter out taskUuids that are APPOINTMENTs
            // Only remove task UUIDs where scheduleItemType[index] !== "APPOINTMENT"
            if (
              patientTaskDetail?.scheduleItemType &&
              Array.isArray(patientTaskDetail?.scheduleItemType) &&
              patientTaskDetail?.taskUuids &&
              Array.isArray(patientTaskDetail.taskUuids)
            ) {
              const taskUuidsToRemove: string[] = [];

              // Iterate through taskUuids and check scheduleItemType at the same index
              patientTaskDetail?.taskUuids?.forEach((taskUuid: string, index: number) => {
                const scheduleType = patientTaskDetail?.scheduleItemType[index];
                // Only include this taskUuid if scheduleItemType is NOT "APPOINTMENT"
                if (scheduleType !== 'APPOINTMENT') {
                  taskUuidsToRemove.push(taskUuid);
                }
              });

              // If there are taskUuids to remove, create a modified patientTaskDetail
              if (taskUuidsToRemove.length > 0) {
                const modifiedTaskDetail = {
                  ...patientTaskDetail,
                  taskUuids: taskUuidsToRemove,
                };
                DeleteTherapistPatient(therapistId, modifiedTaskDetail);
              } else {
                // No non-appointment tasks to remove, don't call delete
                console.log('No non-appointment tasks to remove - all tasks are appointments');
              }
            } else {
              // Normal deletion flow (scheduleItemType is not an array or doesn't exist)
              DeleteTherapistPatient(therapistId, patientTaskDetail);
            }
          }}
        />
      </BootstrapDialog>
      <BootstrapDialog
        onClose={handleEditTimeDialog}
        aria-labelledby="customized-dialog-title"
        open={props.openEditTimeDialog[props.index] || false}
      >
        <EditTaskTime
          providerId={props.providerId}
          handleEditTimeDialog={handleEditTimeDialog}
          therapyInfo={therapyInfo}
          handleDeleteMinuteZero={() => {
            DeleteTherapistPatient(therapistId, patientTaskDetail);
          }}
        />
      </BootstrapDialog>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {permissionData?.currentPermissions?.includes(ADD_SCHEDULER_NOTE) && (
          <MenuItem
            sx={{ gap: '10px', fontWeight: '500', color: '#6C737F' }}
            onClick={() => {
              dispatch(resetCreateTherapistTaskNote());
              handleClose();
              handleOpenTaskNote();
            }}
            data-testid={schedulingTestId('assigned-task-menu-note')}
          >
            <AddNoteIcon />
            {iseNoteAvailable ? 'Edit a Note' : 'Add a Note'}
          </MenuItem>
        )}
        {!isGroupTask && (
          <MenuItem
            sx={{ gap: '10px', fontWeight: '500', color: '#6C737F' }}
            onClick={() => {
              openDeleteModal();
            }}
            data-testid={schedulingTestId('assigned-task-menu-cancel-assignment')}
          >
            <img
              alt=""
              src={trash}
            />
            Cancel Assignment
          </MenuItem>
        )}
        {isAppointmentBook &&
          !patientTaskDetail?.isFutureAdmission &&
          patientTaskDetail?.taskType?.includes('APPOINTMENT') &&
          (() => {
            const appointmentIndex =
              patientTaskDetail?._selectedIndex !== undefined
                ? patientTaskDetail?._selectedIndex
                : Array.isArray(patientTaskDetail?.scheduleItemType)
                ? patientTaskDetail.scheduleItemType.findIndex(
                    (type: string) => type === 'APPOINTMENT'
                  )
                : -1;
            const isAlreadySplit =
              appointmentIndex >= 0 && !!patientTaskDetail?.splitRef?.[appointmentIndex];
            return (
              <MenuItem
                sx={{ gap: '10px', fontWeight: '500', color: '#6C737F' }}
                onClick={() => {
                  handleSpitTask(patientTaskDetail);
                  handleClose();
                }}
                disabled={isAlreadySplit}
                data-testid={schedulingTestId('assigned-task-menu-split')}
              >
                <img
                  alt=""
                  src={divide}
                />
                Split task mins
              </MenuItem>
            );
          })()}
      </Menu>

      <BootstrapDialog
        onClose={() => setOpenDefaultTherapistDialog(false)}
        aria-labelledby="customized-dialog-title"
        open={openDefaultTherapistDialog}
        PaperProps={{ style: { maxWidth: '400px' } }}
      >
        <AssignDefaultTherapist
          therapist={therapist}
          setOpenDefaultTherapistDialog={setOpenDefaultTherapistDialog}
        />
      </BootstrapDialog>
      <SchedulePrintDialog
        providerUUID={therapist?.uuid}
        dateString={moment(props.date, 'DD-MM-YYYY').format('YYYY-MM-DD')}
        open={isPrintDialogOpen}
        onClose={handleClosePrintDialog}
      />

      <ViewAppointmentsModal
        open={isViewAppointmentsOpen}
        onClose={handleCloseViewAppointments}
        therapist={therapist}
        therapistsList={props.therapistsList || []}
        localDate={moment(props.date, 'DD-MM-YYYY').format('YYYY-MM-DD')}
        onEditAppointment={handleEditAppointment}
        onDeleteAppointment={handleDeleteAppointment}
      />

      {selectedAppointmentForEdit && props.therapistsList && (
        <AppointmentSchedulingModal
          open={isEditAppointmentOpen}
          onClose={handleCloseEditAppointment}
          therapists={props.therapistsList}
          selectedTherapist={therapist}
          patient={selectedAppointmentForEdit}
          localDate={moment(props.date, 'DD-MM-YYYY').format('YYYY-MM-DD')}
          onScheduleAppointment={handleScheduleAppointmentSuccess}
          providerId={props.providerId}
          therapistsList={props.therapistsList}
          selectedSortType={props.selectedSortType}
          currentTab={props.currentTab}
          filterTherapistList={props.filterTherapistList}
        />
      )}

      <BootstrapDialog
        PaperProps={{ style: { maxWidth: '600px' } }}
        onClose={() => setOpenSplitMin(false)}
        aria-labelledby="customized-dialog-title"
        fullWidth
        open={openSplitMin}
      >
        <TaskSplitMin
          handleCancelSplitMin={() => setOpenSplitMin(false)}
          handleContinueSplitMin={handleContinueSplitMin}
          patient={splitMinPatient}
          sourceOfSplitMin={sourceOfSplitMin}
        />
      </BootstrapDialog>

      <BootstrapDialog
        open={openSplitConfirm}
        onClose={handleCancelRecurringSplit}
        aria-labelledby="split-recurring-confirm-title"
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={splitMin?.loading}
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle
          id="split-recurring-confirm-title"
          sx={{ fontWeight: 600, fontSize: '18px', color: '#111927' }}
        >
          Split recurring appointment?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: '#111927' }}>
            This appointment is part of a recurring series. Splitting it will remove the split
            appointments from the series. Future edits or deletions to the series will not affect
            these split appointments.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '12px 16px' }}>
          <Button
            onClick={handleCancelRecurringSplit}
            data-testid={schedulingTestId('split-recurring-cancel')}
            variant="text"
            disabled={splitMin?.loading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleConfirmRecurringSplit}
            data-testid={schedulingTestId('split-recurring-confirm')}
            variant="contained"
            loading={splitMin?.loading}
            disabled={splitMin?.loading}
            sx={{ textTransform: 'none' }}
          >
            Confirm Split
          </LoadingButton>
        </DialogActions>
      </BootstrapDialog>

      <WarningModal
        open={overlapModalOpen}
        title="Overlapping Appointment"
        message="This change will create an overlap with another appointment for this patient. Continue?"
        buttonText="Confirm"
        cancelBtn="Cancel"
        primaryCTA
        onClose={handleOverlapCancel}
        handleSaveBtn={handleOverlapConfirm}
      />
    </>
  );
};

export default TherapistInfoV2;
